import { useEffect, useMemo, useState } from "react";
import { apiJson } from "../../shared/lib/api-client.ts";
import { auth, confirmPasswordReset, signOut, verifyPasswordResetCode } from "../lib/firebase.ts";

function getResetErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "code" in error) {
    const code = String((error as { code?: string }).code ?? "");
    if (code === "auth/invalid-action-code") {
      return "This reset link is invalid or has already been used.";
    }
    if (code === "auth/expired-action-code") {
      return "This reset link has expired. Request a fresh email from the login screen.";
    }
    if (code === "auth/weak-password") {
      return "Choose a stronger password with at least 8 characters.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Unable to reset your password.";
}

function readResetState() {
  if (typeof window === "undefined") {
    return { token: "", oobCode: "", email: "" };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    token: params.get("token")?.trim() ?? "",
    oobCode: params.get("oobCode")?.trim() ?? "",
    email: params.get("email")?.trim() ?? "",
  };
}

export function PasswordResetView() {
  const initialState = useMemo(() => readResetState(), []);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(initialState.email || null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    async function validate() {
      if (auth) {
        await signOut(auth).catch(() => undefined);
      }

      if (!initialState.token && !initialState.oobCode) {
        setError("This reset link is missing the required verification code.");
        setValidating(false);
        return;
      }

      try {
        if (initialState.token) {
          const result = await apiJson<{ data?: { email?: string | null } }>(
            "/v1/auth/password-reset/verify",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token: initialState.token }),
            }
          );
          setVerifiedEmail(result.data?.email ?? initialState.email ?? null);
        } else {
          const result = await verifyPasswordResetCode(initialState.oobCode);
          setVerifiedEmail(result.email ?? initialState.email ?? null);
        }
      } catch (validationError) {
        setError(getResetErrorMessage(validationError));
      } finally {
        setValidating(false);
      }
    }

    void validate();
  }, [initialState.email, initialState.oobCode, initialState.token]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting || (!initialState.token && !initialState.oobCode)) return;

    if (password.length < 8) {
      setError("Choose a password with at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (initialState.token) {
        const result = await apiJson<{ data?: { email?: string | null } }>(
          "/v1/auth/password-reset/confirm",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: initialState.token, newPassword: password }),
          }
        );
        const email = encodeURIComponent(
          result.data?.email ?? verifiedEmail ?? initialState.email ?? ""
        );
        setCompleted(true);
        setTimeout(() => {
          window.location.replace(`/?email=${email}&passwordReset=success`);
        }, 1200);
        return;
      }

      const result = await confirmPasswordReset(initialState.oobCode, password);
      const email = encodeURIComponent(result.email ?? verifiedEmail ?? initialState.email ?? "");
      setCompleted(true);
      setTimeout(() => {
        window.location.replace(`/?email=${email}&passwordReset=success`);
      }, 1200);
    } catch (submitError) {
      setError(getResetErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  }

  const hasResetCode = Boolean(initialState.token || initialState.oobCode);
  const canSubmitReset = Boolean(hasResetCode && (verifiedEmail || initialState.email));

  return (
    <div className="login-container">
      <div className="login-card password-reset-card">
        <div className="login-eyebrow">Legion Scout</div>
        <h1 className="login-title">Reset Password</h1>
        <p className="login-subtitle">
          {verifiedEmail
            ? `Resetting access for ${verifiedEmail}`
            : "Choose a new password to continue."}
        </p>

        {validating ? (
          <div className="loading">
            <div className="spinner" />
            <span className="loading-text">Validating secure link</span>
          </div>
        ) : completed ? (
          <div className="login-success">
            Password updated. Redirecting you back to the Legion Scout login screen.
          </div>
        ) : canSubmitReset ? (
          <form className="login-form" onSubmit={handleSubmit}>
            <label className="login-label" htmlFor="reset-password">
              New Password
            </label>
            <input
              id="reset-password"
              className="login-input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />

            <label className="login-label" htmlFor="reset-password-confirm">
              Confirm Password
            </label>
            <input
              id="reset-password-confirm"
              className="login-input"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />

            {error && <div className="login-error">{error}</div>}

            <button type="submit" className="btn login-submit" disabled={submitting}>
              {submitting ? "Updating..." : "Save New Password"}
            </button>

            <button
              type="button"
              className="btn btn-secondary login-secondary"
              onClick={() => window.location.replace("/")}
            >
              Back to Login
            </button>
          </form>
        ) : (
          <div className="login-form">
            {error && <div className="login-error">{error}</div>}
            <button
              type="button"
              className="btn btn-secondary login-secondary"
              onClick={() => window.location.replace("/")}
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
